/**
 * Carnatic raga reference — the 72 Melakarta (sampoorna) ragas plus common Janya ragas.
 *
 * The 72 Melakarta swara-sets are GENERATED from the chakra formula rather than
 * hand-transcribed, so they are provably consistent (see tests). Notation uses the
 * 12-symbol collapsed set from `music.ts` (G1≡R2→'R', N1≡D2→'D').
 */
/** The 6 Rishabham–Gandharam combinations, in chakra order (Indu…Rutu). */
const RIGA_COMBOS = [
    ['r', 'R'], // R1 G1
    ['r', 'g'], // R1 G2
    ['r', 'G'], // R1 G3
    ['R', 'g'], // R2 G2
    ['R', 'G'], // R2 G3
    ['g', 'G'], // R3 G3
];
/** The 6 Dhaivatham–Nishadham combinations, in order. */
const DHANI_COMBOS = [
    ['d', 'D'], // D1 N1
    ['d', 'n'], // D1 N2
    ['d', 'N'], // D1 N3
    ['D', 'n'], // D2 N2
    ['D', 'N'], // D2 N3
    ['n', 'N'], // D3 N3
];
/** Canonical names of the 72 Melakarta ragas, indexed 1..72. */
export const MELAKARTA_NAMES = [
    'Kanakangi',
    'Ratnangi',
    'Ganamurti',
    'Vanaspati',
    'Manavati',
    'Tanarupi',
    'Senavati',
    'Hanumatodi',
    'Dhenuka',
    'Natakapriya',
    'Kokilapriya',
    'Rupavati',
    'Gayakapriya',
    'Vakulabharanam',
    'Mayamalavagowla',
    'Chakravakam',
    'Suryakantam',
    'Hatakambari',
    'Jhankaradhvani',
    'Natabhairavi',
    'Keeravani',
    'Kharaharapriya',
    'Gourimanohari',
    'Varunapriya',
    'Mararanjani',
    'Charukesi',
    'Sarasangi',
    'Harikambhoji',
    'Dheerasankarabharanam',
    'Naganandini',
    'Yagapriya',
    'Ragavardhini',
    'Gangeyabhushani',
    'Vagadheeswari',
    'Shulini',
    'Chalanata',
    'Salagam',
    'Jalarnavam',
    'Jhalavarali',
    'Navaneetam',
    'Pavani',
    'Raghupriya',
    'Gavambhodi',
    'Bhavapriya',
    'Shubhapantuvarali',
    'Shadvidamargini',
    'Suvarnangi',
    'Divyamani',
    'Dhavalambari',
    'Namanarayani',
    'Kamavardhini',
    'Ramapriya',
    'Gamanashrama',
    'Vishwambari',
    'Shamalangi',
    'Shanmukhapriya',
    'Simhendramadhyamam',
    'Hemavati',
    'Dharmavati',
    'Neetimati',
    'Kantamani',
    'Rishabhapriya',
    'Latangi',
    'Vachaspati',
    'Mechakalyani',
    'Chitrambari',
    'Sucharitra',
    'Jyotiswarupini',
    'Dhatuvardhani',
    'Nasikabhushani',
    'Kosalam',
    'Rasikapriya',
];
/** Generate the sampoorna swara set for a Melakarta number (1..72). */
export function melakartaSwaras(mela) {
    if (!Number.isInteger(mela) || mela < 1 || mela > 72) {
        throw new Error(`Melakarta number out of range: ${mela}`);
    }
    const idx = (mela - 1) % 36; // 0..35 within a madhyama half
    const madhyama = mela <= 36 ? 'm' : 'M';
    const riga = RIGA_COMBOS[Math.floor(idx / 6)];
    const dhani = DHANI_COMBOS[idx % 6];
    return ['S', riga[0], riga[1], madhyama, 'P', dhani[0], dhani[1]];
}
/** All 72 Melakarta ragas as `Raga` records. */
export const MELAKARTAS = MELAKARTA_NAMES.map((name, i) => ({
    name,
    melakarta: i + 1,
    swaras: melakartaSwaras(i + 1),
}));
/**
 * Common Janya ragas. `melakarta` points to the parent; `swaras` is the set of
 * allowed swaras (sufficient for per-note scale membership — v1 does not grade
 * arohana/avarohana vakra or gamaka).
 */
export const JANYA_RAGAS = [
    { name: 'Mohanam', melakarta: 28, swaras: ['S', 'R', 'G', 'P', 'D'] },
    { name: 'Hamsadhwani', melakarta: 29, swaras: ['S', 'R', 'G', 'P', 'N'] },
    { name: 'Hindolam', melakarta: 20, swaras: ['S', 'g', 'm', 'd', 'n'] },
    { name: 'Madhyamavati', melakarta: 22, swaras: ['S', 'R', 'm', 'P', 'n'] },
    { name: 'Sriranjani', melakarta: 22, swaras: ['S', 'R', 'g', 'm', 'D', 'n'] },
    { name: 'Abhogi', melakarta: 22, swaras: ['S', 'R', 'g', 'm', 'D'] },
    { name: 'Suddha Dhanyasi', melakarta: 22, swaras: ['S', 'g', 'm', 'P', 'n'] },
    { name: 'Anandabhairavi', melakarta: 20, swaras: ['S', 'R', 'g', 'm', 'P', 'D', 'n'] },
    { name: 'Bhairavi', melakarta: 20, swaras: ['S', 'R', 'g', 'm', 'P', 'd', 'D', 'n'] },
    { name: 'Kambhoji', melakarta: 28, swaras: ['S', 'R', 'G', 'm', 'P', 'D', 'n'] },
    { name: 'Kedaragowla', melakarta: 28, swaras: ['S', 'R', 'm', 'P', 'n', 'N'] },
    { name: 'Arabhi', melakarta: 29, swaras: ['S', 'R', 'G', 'm', 'P', 'D', 'N'] },
    { name: 'Bilahari', melakarta: 29, swaras: ['S', 'R', 'G', 'P', 'D', 'm', 'N'] },
    { name: 'Saveri', melakarta: 15, swaras: ['S', 'r', 'm', 'P', 'd'] },
    { name: 'Malahari', melakarta: 15, swaras: ['S', 'r', 'm', 'P', 'd'] },
    { name: 'Revathi', melakarta: 2, swaras: ['S', 'r', 'm', 'P', 'n'] },
    { name: 'Nata', melakarta: 36, swaras: ['S', 'g', 'G', 'm', 'P', 'N'] },
    { name: 'Kapi', melakarta: 22, swaras: ['S', 'R', 'g', 'm', 'P', 'D', 'n', 'N'] },
    // Bhashanga janya of Kharaharapriya: kakali nishada (N3, 'N') in the ascent,
    // kaisiki nishada (N2, 'n') in the descent; no gandhara or dhaivata.
    // Ārohaṇa S R2 M1 P N3 Ṡ · Avarohaṇa Ṡ N2 P M1 R2 S.
    {
        name: 'Brindavana Saranga',
        melakarta: 22,
        swaras: ['S', 'R', 'm', 'P', 'n', 'N'],
        arohana: ['S', 'R', 'm', 'P', 'N'],
        avarohana: ['S', 'n', 'P', 'm', 'R'],
    },
];
/** Every raga (Melakarta + Janya). */
export const ALL_RAGAS = [...MELAKARTAS, ...JANYA_RAGAS];
const RAGA_BY_NAME = new Map(ALL_RAGAS.map((r) => [r.name.toLowerCase(), r]));
/** Case-insensitive raga lookup by name. */
export function getRaga(name) {
    return RAGA_BY_NAME.get(name.trim().toLowerCase());
}
//# sourceMappingURL=ragas.js.map